import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import RemindersBlock from "@/components/RemindersBlock";

export default function MarketingTab({ API, t }) {
  const [triggers, setTriggers] = useState([]);
  const [salon, setSalon] = useState(null);
  const [react, setReact] = useState({ reactivation_enabled: false, reactivation_days: 45, reactivation_message: "", reactivation_channel: "whatsapp" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/marketing/triggers`).then((r) => setTriggers(r.data.triggers || [])).catch(() => {});
    axios.get(`${API}/salons`).then((r) => {
      const s = (r.data || [])[0];
      if (s) {
        setSalon(s);
        setReact({
          reactivation_enabled: s.reactivation_enabled === true,
          reactivation_days: s.reactivation_days || 45,
          reactivation_message: s.reactivation_message || "",
          reactivation_channel: s.reactivation_channel || "whatsapp",
        });
      }
    }).catch(() => {});
  }, [API]);

  const saveTriggers = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/marketing/triggers`, { triggers });
      setTriggers(r.data.triggers || []);
      toast.success(t("mk.saved"));
    } catch (e) { toast.error("Error"); }
    setSaving(false);
  };

  const saveReactivation = async () => {
    if (!salon) return;
    setSaving(true);
    try {
      await axios.put(`${API}/salons/${salon.id}`, {
        ...react, reactivation_days: parseInt(react.reactivation_days) || 45,
      });
      toast.success(t("mk.saved"));
    } catch (e) { toast.error("Error"); }
    setSaving(false);
  };

  const upd = (i, field, val) => setTriggers((prev) => prev.map((tg, j) => (j === i ? { ...tg, [field]: val } : tg)));

  return (
    <div className="space-y-8 text-black" data-testid="marketing-tab">
      <RemindersBlock API={API} t={t} salon={salon} />

      {/* Keyword triggers */}
      <div className="bg-white border-2 border-black rounded-xl p-4 sm:p-6">
        <h3 className="text-xl font-semibold">🎯 {t("mk.triggersTitle")}</h3>
        <p className="text-sm text-gray-600 mt-1 mb-4">{t("mk.triggersHint")}</p>
        <div className="space-y-3">
          {triggers.map((tg, i) => (
            <div key={tg.id || i} className="flex flex-col sm:flex-row gap-2 items-start border border-gray-200 rounded-lg p-3" data-testid={`mk-trigger-row-${i}`}>
              <div className="w-full sm:w-48 shrink-0">
                <Label className="text-xs font-medium">{t("mk.keyword")}</Label>
                <Input data-testid={`mk-keyword-${i}`} value={tg.keyword}
                  onChange={(e) => upd(i, "keyword", e.target.value)}
                  placeholder="комплимент" className="mt-1 border-2 border-black" />
                <label className="flex items-center gap-2 mt-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={tg.active !== false}
                    onChange={(e) => upd(i, "active", e.target.checked)} />
                  {t("mk.active")}
                </label>
              </div>
              <div className="flex-1 w-full">
                <Label className="text-xs font-medium">{t("mk.response")}</Label>
                <textarea data-testid={`mk-response-${i}`} value={tg.response}
                  onChange={(e) => upd(i, "response", e.target.value)}
                  placeholder={t("mk.responsePh")}
                  className="w-full min-h-[80px] p-2.5 border-2 border-black rounded-lg text-sm mt-1" />
              </div>
              <button data-testid={`mk-delete-${i}`} onClick={() => setTriggers((p) => p.filter((_, j) => j !== i))}
                className="p-2 text-red-500 hover:bg-red-50 rounded-md mt-5 shrink-0">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          <Button data-testid="mk-add-trigger-btn" variant="outline"
            onClick={() => setTriggers((p) => [...p, { keyword: "", response: "", active: true }])}
            className="border-2 border-black bg-white text-black hover:bg-gray-100">
            <Plus className="w-4 h-4 mr-1" />{t("mk.addTrigger")}
          </Button>
          <Button data-testid="mk-save-triggers-btn" onClick={saveTriggers} disabled={saving}
            className="bg-black text-white hover:bg-gray-800">
            {t("mk.save")}
          </Button>
        </div>
      </div>

      {/* Reactivation (moved from settings) */}
      <div className="bg-white border-2 border-black rounded-xl p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-xl font-semibold">🔄 {t("settings.reactivationTitle")}</h3>
            <p className="text-sm text-gray-600 mt-1">{t("settings.reactivationHint")}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input type="checkbox" data-testid="reactivation-toggle" checked={react.reactivation_enabled}
              onChange={(e) => setReact({ ...react, reactivation_enabled: e.target.checked })}
              className="sr-only peer" />
            <div className="w-11 h-6 bg-gray-300 peer-checked:bg-black rounded-full peer transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
          </label>
        </div>
        {react.reactivation_enabled && (
          <div className="mt-4 space-y-3" data-testid="reactivation-fields">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-medium text-sm">{t("settings.reactivationDays")}</Label>
                <Input data-testid="reactivation-days-input" type="number" min="7" step="1"
                  value={react.reactivation_days}
                  onChange={(e) => setReact({ ...react, reactivation_days: e.target.value })}
                  className="mt-1 border-2 border-black" placeholder="45" />
              </div>
              <div>
                <Label className="font-medium text-sm">{t("settings.reactivationChannel")}</Label>
                <select data-testid="reactivation-channel-select" value={react.reactivation_channel}
                  onChange={(e) => setReact({ ...react, reactivation_channel: e.target.value })}
                  className="mt-1 w-full h-10 border-2 border-black rounded-md px-2 text-sm bg-white">
                  <option value="whatsapp">WhatsApp</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="font-medium text-sm">{t("settings.reactivationMessage")}</Label>
              <textarea data-testid="reactivation-message-input" value={react.reactivation_message || ""}
                onChange={(e) => setReact({ ...react, reactivation_message: e.target.value })}
                className="w-full min-h-[70px] p-3 border-2 border-black rounded-lg text-black text-sm mt-1"
                placeholder={t("settings.reactivationPlaceholder")} />
              <p className="text-xs text-gray-600 mt-1">{t("settings.reactivationTip")}</p>
            </div>
          </div>
        )}
        <Button data-testid="mk-save-reactivation-btn" onClick={saveReactivation} disabled={saving || !salon}
          className="mt-4 bg-black text-white hover:bg-gray-800">
          {t("mk.save")}
        </Button>
      </div>

    </div>
  );
}
